import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, styled, Link, Box, IconButton, useTheme } from '@mui/material';
import { Transactions } from '../../../../../../models/dashboard/Transactions';
import { getPdfTransactions, getTransactions, getTransactionsDataFilter } from '../../../../../../hooks/actions/dashboard';
import { formatDate } from '../../../../../../helpers/FormatDate';
import { GRAY_LIGHT, GREEN_MAIN, GREEN_LOGO } from '../../../../../../theme/colors';
import { formatToTwoDecimals } from '../../../../../../helpers/numberFormats';
import { useTranslationContext } from '../../../../../../hooks/translations/translationsProvider';
import settings from '../../../../../../utilities/settings';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import PrintIcon from '@mui/icons-material/Print';
import { getUserTokens } from '../../../../../../hooks/actions/admin';
import { CustomTableContainer } from '../../../../style';

const TableCellDefault = styled(TableCell)(({ theme }) => ({
    backgroundColor: `transparent`,
    fontWeight:'bold',
    color: GREEN_MAIN,
    border:'0px',
    whiteSpace: 'nowrap'
}));

const TableCellRowsDefault = styled(TableCell)(({ theme }) => ({
    fontSize:'0.85rem',
    whiteSpace: 'nowrap', 
    overflow: 'hidden', 
    textOverflow: 'ellipsis',
    borderBottom:'0px',
    padding:'8px 16px'
}));

const getDate365DaysAgo = (): Date => {
    const date = new Date();
    date.setDate(date.getDate() - 365);
    return date;
};

const getDateOneMonthAgo = (): Date => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date;
};

interface UserTokensTableProps {
    userId:string
    reloadData:  number;
}

const UserTokensTable: React.FC<UserTokensTableProps> = ({userId, reloadData}) => {
    const now = new Date();
    const theme = useTheme();
    const [transactions, setTransactions] = useState<Transactions[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const {t} = useTranslationContext();
    const [dateFrom, setDateFrom] = useState<Date>(getDate365DaysAgo());
    const [dateTo, setDateTo] = useState<Date>(() => {
        const now = new Date();
        now.setHours(23, 59, 59, 999); // Imposta l'orario alle 23:59:59.999
        return now; // Restituisce l'oggetto Date correttamente inizializzato
      });

    const fetchTransactions = () => {
        getUserTokens(userId, page, rowsPerPage, dateFrom, dateTo)
            .then(response => {
                setTransactions(response.transactions);
                setTotalRecords(response.totalRecords);
            })
            .catch(e => {
                console.log(e)
            });
    };

    useEffect(() => {
        if(dateFrom >= dateTo){
            setDateFrom(dayjs(dateTo).add(-1, 'day').toDate())
        }
        fetchTransactions();
    }, [page, rowsPerPage, dateFrom, dateTo, reloadData]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDateFromChange = (date: dayjs.Dayjs | null) => {
        if (date) {
            setDateFrom(date.toDate()); // Converti in Date
            setPage(0); // Reset to first page on date change
        }
    };

    const handleDateToChange = (date: dayjs.Dayjs | null) => {
        if (date) {
            setDateTo(date.toDate()); // Converti in Date
            setPage(0); // Reset to first page on date change
        }
    };

    return (
        <Paper sx={{ 
            width: '100%', 
            overflow: 'hidden', 
            borderColor: 'transparent',
            maxWidth: '100%',
            boxSizing: 'border-box',
            boxShadow: 'none'
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label={t("Data Da")}
                        value={dayjs(dateTo)}
                        onChange={(date) => handleDateToChange(date)}
                        disableFuture
                        views={['day', 'month', 'year']}
                        format="DD/MM/YYYY"
                        slotProps={{
                            textField: {
                                required: true,
                                error: false,
                                helperText: ''
                            }
                        }}
                        sx={{
                            '.MuiInputBase-root': { 
                                height: 48,
                                marginRight:'8px'
                            }
                        }}
                    />
                    <DatePicker
                        label={t("Data A")}
                        value={dayjs(dateFrom)}
                        onChange={(date) => handleDateFromChange(date)}
                        disableFuture
                        views={['day', 'month', 'year']}
                        format="DD/MM/YYYY"
                        maxDate={dayjs(dateTo).subtract(1, 'day')}
                        slotProps={{
                            textField: {
                                required: true,
                                error: false,
                                helperText: ''
                            }
                        }}
                        sx={{
                            '.MuiInputBase-root': { 
                                height: 48,
                                marginLeft:'8px'
                            }
                        }}
                    />
                    {/* <IconButton
                        onClick={() => {getPdfTransactions(dateFrom, dateTo).finally()}}
                        sx={{ }}
                    >
                        <PrintIcon />
                    </IconButton> */}
                </LocalizationProvider>
            </Box>
            <CustomTableContainer>
                <Table aria-label="transactions table">
                    <TableHead>
                        <TableRow>
                            <TableCellDefault>{t("Ammontare Asso")}</TableCellDefault>
                            <TableCellDefault>{t("Data di richiesta")}</TableCellDefault>
                            <TableCellDefault>{t("Data di conferma")}</TableCellDefault>
                            <TableCellDefault>{t("Tipo di Transazione")}</TableCellDefault>
                            <TableCellDefault>{t("Informazioni")}</TableCellDefault>
                            <TableCellDefault>{t("Hash")}</TableCellDefault>
                            <TableCellDefault>{t("Indirizzo Wallet")}</TableCellDefault>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {transactions && transactions.map((transaction) => (
                            <TableRow key={transaction.hash}>
                                <TableCellRowsDefault
                                    sx={{
                                        color: transaction.tokensAmount < 0 ? 'red' : undefined
                                    }}
                                >{formatToTwoDecimals(transaction.tokensAmount)}</TableCellRowsDefault>
                                <TableCellRowsDefault>{formatDate(transaction.dataEvent)}</TableCellRowsDefault>
                                <TableCellRowsDefault
                                    sx={{
                                        color: transaction.dataConfirmed === null ? 'orange' : undefined
                                    }}
                                >{ transaction.dataConfirmed === null ? "Pending.." : formatDate(transaction.dataConfirmed)}</TableCellRowsDefault>
                                <TableCellRowsDefault>{t(transaction.type)}</TableCellRowsDefault>
                                <TableCellRowsDefault>{t(transaction.info)}</TableCellRowsDefault>
                                <TableCellRowsDefault>
                                    {transaction.hash ? (
                                        <Link
                                            href={`${settings.Token.Blockchain.ExplorerUrl}/tx/${transaction.hash}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {transaction.hash}
                                        </Link>
                                    ) : (
                                        <>
                                            {transaction.hash}
                                        </>
                                    )}
                                </TableCellRowsDefault>

                                <TableCellRowsDefault>
                                    {transaction.addressWallet ? (
                                        <Link
                                            href={`${settings.Token.Blockchain.ExplorerUrl}/address/${transaction.addressWallet}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {transaction.addressWallet}
                                        </Link>
                                    ) : (
                                        <>
                                            {transaction.addressWallet}
                                        </>
                                    )}
                                </TableCellRowsDefault>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CustomTableContainer>
            
            <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t("Risultati per pagina")}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t("di")} ${count}`}
                sx={{
                  color:GREEN_MAIN,
                  "& .MuiTablePagination-select": {
                      "&:focus": {
                        backgroundColor: "transparent",
                      },
                  },
                  // This targets the dropdown arrow icon
                  "& .MuiTablePagination-selectIcon": {
                      color: GREEN_MAIN,
                  },
                  // This targets the navigation arrows
                  "& .MuiTablePagination-actions": {
                      "& .MuiIconButton-root": {
                        color: GREEN_MAIN,
                      },
                  },
                  // Optional: Style the text to match
                  "& .MuiTablePagination-displayedRows": {
                      color: GREEN_MAIN,
                  },
                  "& .MuiTablePagination-selectLabel": {
                      color: GREEN_MAIN,
                  },
                  // Add scrollbar styling
                  "&::-webkit-scrollbar": {
                    height: "8px",
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: theme.palette.mode === "light" ? "#f1f1f1" : "#2d2d2d",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  "&::-webkit-scrollbar-button": {
                    display: "none !important",
                    width: "0px",
                    height: "0px",
                  },
                  scrollbarWidth: "thin",
                  scrollbarColor: `${GREEN_MAIN} ${theme.palette.mode === "light" ? "#f1f1f1" : "#2d2d2d"}`,
                  msOverflowStyle: "none",
                  overflowX: "auto",
                  maxWidth: "100%",
                }}
            />
        </Paper>
    );
};

export default UserTokensTable;